import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { hapticLight } from "../../utils/haptics"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

/* One overlay for every modal surface (Dialog / Sheet / AlertDialog / Drawer) */
export const OVERLAY_CLASS =
  "fixed inset-0 z-overlay bg-gray-950/40 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 duration-base"

/* One close control for every modal surface */
export const CLOSE_BUTTON_CLASS =
  "inline-flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors duration-fast hover:bg-gray-200 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none"

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay ref={ref} className={cn(OVERLAY_CLASS, className)} {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/* Desktop (md+): centered, rounded-3xl, shadow-modal. Mobile + portrait tablet (<md): full-width bottom sheet
   with a drag-to-dismiss grabber and safe-area padding. `hideClose` for content that
   renders its own header close control. */
const DialogContent = React.forwardRef(({ className, children, hideClose = false, ...props }, ref) => {
  const closeRef = React.useRef(null)
  const sheetRef = React.useRef(null)
  const drag = React.useRef({ startY: 0, delta: 0, active: false })

  const setRefs = React.useCallback((node) => {
    sheetRef.current = node
    if (typeof ref === "function") ref(node)
    else if (ref) ref.current = node
  }, [ref])

  const onGrabStart = (e) => {
    drag.current = { startY: e.touches[0].clientY, delta: 0, active: true }
    if (sheetRef.current) sheetRef.current.style.transition = "none"
  }
  const onGrabMove = (e) => {
    if (!drag.current.active) return
    const delta = Math.max(0, e.touches[0].clientY - drag.current.startY)
    drag.current.delta = delta
    if (sheetRef.current) sheetRef.current.style.transform = `translateY(${delta}px)`
  }
  const onGrabEnd = () => {
    if (!drag.current.active) return
    const { delta } = drag.current
    drag.current.active = false
    const sheet = sheetRef.current
    if (!sheet) return
    sheet.style.transition = "transform 0.2s ease-out"
    if (delta > 90) {
      sheet.style.transform = "translateY(110%)"
      hapticLight()
      setTimeout(() => closeRef.current?.click(), 160)
    } else {
      sheet.style.transform = ""
    }
  }

  return (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={setRefs}
      className={cn(
        // ── Shared ────────────────────────────────────────────────────────────
        "fixed z-overlay bg-surface shadow-modal text-foreground",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        // ── Mobile: bottom sheet ───────────────────────────────────────────
        "max-md:bottom-0 max-md:left-0 max-md:right-0 max-md:top-auto",
        "max-md:w-full max-md:!max-w-full max-md:translate-x-0 max-md:translate-y-0",
        "max-md:max-h-[94dvh] max-md:overflow-y-auto",
        "max-md:rounded-t-3xl max-md:rounded-b-none",
        "max-md:data-[state=open]:slide-in-from-bottom",
        "max-md:data-[state=closed]:slide-out-to-bottom",
        "max-md:pt-8 max-md:pb-[max(1.5rem,env(safe-area-inset-bottom))]",
        // ── Desktop: centered dialog ───────────────────────────────────────
        "md:left-[50%] md:top-[50%] md:translate-x-[-50%] md:translate-y-[-50%]",
        "sm:max-w-lg md:max-h-[90vh] md:rounded-3xl md:w-full",
        "md:data-[state=closed]:zoom-out-95 md:data-[state=open]:zoom-in-95",
        "md:data-[state=open]:slide-in-from-left-1/2 md:data-[state=open]:slide-in-from-top-[48%]",
        "md:data-[state=closed]:slide-out-to-left-1/2 md:data-[state=closed]:slide-out-to-top-[48%]",
        // ── Timing / padding ──────────────────────────────────────────────
        "duration-slow p-6 gap-4 grid",
        className
      )}
      style={{ WebkitOverflowScrolling: 'touch' }}
      {...props}>
      {/* Mobile-only grabber: drag down to dismiss */}
      <div
        className="md:hidden absolute top-0 inset-x-0 h-7 z-40 flex items-start justify-center pt-2 touch-none"
        onTouchStart={onGrabStart}
        onTouchMove={onGrabMove}
        onTouchEnd={onGrabEnd}
        data-testid="dialog-sheet-grabber"
      >
        <span className="w-10 h-1.5 rounded-full bg-gray-300" />
      </div>
      {children}
      {!hideClose && (
        <DialogPrimitive.Close
          ref={closeRef}
          className={cn(CLOSE_BUTTON_CLASS, "absolute right-3 top-3 md:right-4 md:top-4 z-50 max-md:top-5")}>
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
      {hideClose && <DialogPrimitive.Close ref={closeRef} className="hidden" tabIndex={-1} aria-hidden="true" />}
    </DialogPrimitive.Content>
  </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-left pr-10", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse gap-2 md:flex-row md:justify-end", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-tight tracking-tight text-foreground", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-foreground-muted", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
